<template>
    <a-card style="max-width: 500px;margin: 0 auto;" :head-style="{ textAlign: 'center' }" :title="$t('text.regNewAcc')">
        <a-form
            ref="form"
            :model="formModel"
            @finish="onFinish"
            :rules="formRule"
            :scrollToFirstError="true"
            layout="vertical"
        >
            <a-form-item :label="$t('labelText.username')" name="name">
                <a-input v-model:value="formModel.name" :autofocus="true" />
            </a-form-item>

            <a-form-item :label="$t('labelText.password')" name="password">
                <a-input-password v-model:value="formModel.password" />
            </a-form-item>

            <a-form-item :label="$t('labelText.password_confirm')" name="password_confirm">
                <a-input-password v-model:value="formModel.password_confirm" />
            </a-form-item>

            <a-form-item :label="$t('labelText.email')" name="email">
                <a-input v-model:value="formModel.email" />
            </a-form-item>

            <a-form-item :label="$t('labelText.password2')" name="password2">
                <a-input-password v-model:value="formModel.password2" />
            </a-form-item>

            <a-form-item :label="$t('labelText.password2_confirm')" name="password2_confirm">
                <a-input-password v-model:value="formModel.password2_confirm" />
            </a-form-item>

            <a-form-item name="agree">
                <a-checkbox v-model:checked="formModel.agree">{{ $t('labelText.agreeTos') }}</a-checkbox>
            </a-form-item>

            <a-form-item>
                <a-button type="primary" html-type="submit" block :loading="isLoading">{{ $t('text.register') }}</a-button>
            </a-form-item>
        </a-form>
    </a-card>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, } from 'vue';
import { useRouter } from 'vue-router';
import {
    Button, Form, Input, InputPassword, Checkbox, Card, Modal,
} from 'ant-design-vue';

import type { RuleObject } from 'ant-design-vue/lib/form';

import ApiRequest from '@/utils/ApiRequest';
import { RouteName, } from '@/router';
import { useI18n } from 'vue-i18n';

interface FormModel {
    name: string;
    password: string;
    password_confirm: string;
    password2: string;
    password2_confirm: string;
    email: string;
    agree: boolean;
}

export default defineComponent({
    components: {
        AButton: Button,
        AForm: Form,
        AFormItem: Form.Item,
        AInput: Input,
        AInputPassword: InputPassword,
        ACheckbox: Checkbox,
        ACard: Card,
    },

    setup() {
        const router = useRouter();
        const { t } = useI18n();
        const form = ref<typeof Form>();

        const formModel = reactive<FormModel>({
            name: '',
            password: '',
            password_confirm: '',
            password2: '',
            password2_confirm: '',
            email: '',
            agree: false,
        });

        const formExtraError: Record<keyof FormModel, string> = {
            name: '',
            email: '',
            password: '',
            password_confirm: '',
            password2: '',
            password2_confirm: '',
            agree: '',
        };
        const formRule = reactive<Record<keyof FormModel, RuleObject[]>>({
            name: [
                {
                    required: true,
                },
                {
                    min: 6,
                    max: 16,
                    trigger: ['blur', 'change'],
                },
                {
                    pattern: /^[a-zA-Z0-9_.]+$/,
                    trigger: ['blur', 'change'],
                },
                {
                    validator: (_rule, _value, callback) => callback(formExtraError.name || undefined),
                },
            ],
            password: [
                {
                    required: true,
                },
                {
                    min: 8,
                    trigger: ['blur', 'change'],
                },
            ],
            password_confirm: [
                {
                    required: true,
                    trigger: 'change',
                },
                {
                    required: true,
                    trigger: ['blur', 'change'],
                },
                {
                    validator: (_rule, value, callback) => callback((value && (value !== formModel.password)) ? t('message.passwordNotMatch') : undefined),
                    trigger: ['blur', 'change'],
                }
            ],
            password2: [
                {
                    required: true,
                    trigger: 'change',
                },
                {
                    min: 8,
                    trigger: ['blur', 'change'],
                },
                {
                    validator: (_rule, value, callback) => {
                        let errTxt: string | undefined = undefined;
                        if (value) {
                            if (value === formModel.password) {
                                errTxt = t('message.password2MustNotEqualPassword');
                            }
                        }

                        callback(errTxt);
                    },
                },
            ],
            password2_confirm: [
                {
                    required: true,
                    trigger: 'change',
                },
                {
                    validator: (_rule, value, callback) => {
                        let errTxt: string | undefined = undefined;
                        if (value) {
                            if (value !== formModel.password2) {
                                errTxt = t('message.passwordNotMatch');
                            }
                        }

                        callback(errTxt);
                    },
                    trigger: ['blur', 'change'],
                },
            ],
            email: [
                {
                    required: true,
                },
                {
                    type: 'email',
                    trigger: ['blur', 'change'],
                },
                {
                    validator: (_rule, _value, callback) => callback(formExtraError.email || undefined),
                },
            ],
            agree: [
                {
                    required: true,
                    trigger: ['blur', 'change'],
                },
                {
                    validator: (_rule, value, callback) => callback(!value ? t('message.mustAgreeTos') : undefined),
                    trigger: ['blur', 'change'],
                },
            ],
        });

        const isLoading = ref(false);

        const onFinish = (values: FormModel) => {
            isLoading.value = true;

            ApiRequest.post('/register', values)
                .then(res => {
                    Modal.info({
                        content: t('message.regNewAccSuccess', {accname: res.data.data.name}),
                        onOk: () => router.push({ name: RouteName.LOGIN }),
                        okText: t('text.login'),
                    });
                })
                .catch(err => {
                    ApiRequest.handleError(err, formExtraError, form.value);
                })
                .finally(() => {
                    isLoading.value = false;
                });
        };

        return {
            RouteName,

            onFinish,

            formModel,
            formRule,
            formExtraError,
            form,

            isLoading,
        };
    },
})
</script>
